import Avatar, { type AvatarProps } from "@mui/material/Avatar";
import { dividerClasses } from "@mui/material/Divider";
import { listItemIconClasses } from "@mui/material/ListItemIcon";
import { listItemTextClasses } from "@mui/material/ListItemText";
import Menu, { type MenuProps } from "@mui/material/Menu";
import { menuItemClasses } from "@mui/material/MenuItem";
import type { Theme } from "@mui/material/styles";

import { SHELL_BAR, SHELL_SURFACE, shellColors } from "./tokens";

const EXPANDED_MEDIA = "@media (min-width:840px)";

/**
 * The signed-in user's 26px avatar on the dark bar. The initials fallback is
 * slate for business and emerald for platform, as in each app's mockup.
 */
export function ShellAvatar({
  sx,
  tone = "slate",
  ...props
}: AvatarProps & { tone?: "slate" | "emerald" }) {
  return (
    <Avatar
      {...props}
      sx={[
        {
          width: 26,
          height: 26,
          fontSize: 10.5,
          fontWeight: 700,
          bgcolor: tone === "emerald" ? SHELL_BAR.avatarEmerald : SHELL_BAR.avatarFallback,
          color: tone === "emerald" ? SHELL_BAR.avatarEmeraldText : SHELL_BAR.avatarFallbackText,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}

/**
 * The top-bar user menu frame (mockup 14c): a 288px card with 32px inset rows,
 * 13px items and 18px slate icons. Rows keep the touch target below 840px.
 */
export function ShellMenu({ slotProps, ...props }: MenuProps) {
  return (
    <Menu
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      {...props}
      slotProps={{
        ...slotProps,
        paper: {
          sx: (theme: Theme) => ({
            width: 288,
            maxWidth: "calc(100vw - 16px)",
            mt: 0.5,
            p: "6px",
            borderRadius: "10px",
            border: 1,
            boxShadow: SHELL_SURFACE.popShadow,
            ...shellColors(theme, { borderColor: "line" }),
          }),
        },
        list: {
          ...(slotProps?.list as object | undefined),
          sx: (theme: Theme) => ({
            py: 0,
            [`& .${menuItemClasses.root}`]: {
              gap: "10px",
              minHeight: 44,
              px: "10px",
              py: 0,
              borderRadius: "6px",
              fontSize: 13,
              ...shellColors(theme, { color: "ink2" }),
              [EXPANDED_MEDIA]: { minHeight: 32 },
            },
            [`& .${menuItemClasses.root}:hover, & .${menuItemClasses.focusVisible}`]:
              shellColors(theme, { backgroundColor: "fill", color: "ink" }),
            // MenuItem sets a 36px icon column; the mockup uses a 10px gap.
            [`& .${menuItemClasses.root} .${listItemIconClasses.root}`]: {
              minWidth: 0,
              fontSize: 18,
              ...shellColors(theme, { color: "muted" }),
            },
            [`& .${menuItemClasses.root} .${listItemIconClasses.root} svg`]: {
              fontSize: "inherit",
            },
            [`& .${listItemTextClasses.primary}`]: { fontSize: 13 },
            [`& .${dividerClasses.root}`]: {
              my: "4px",
              ...shellColors(theme, { borderColor: "line2" }),
            },
          }),
        },
      }}
    />
  );
}
